/**
 * DomainStatusBadge Component
 *
 * Visual status indicator for custom domain setup progress.
 * Uses MUI Chip with color coding and optional loading spinner.
 *
 * Pattern reference: src/modules/webplacements/components/WebPlacementStatusBadge.tsx
 */

import React from 'react';
import { Chip, CircularProgress, type ChipProps } from '@mui/material';
import type { CustomDomainStatus } from '../types/customDomain.types';

/**
 * Status configuration
 */
interface StatusConfig {
  /** Display label */
  label: string;
  /** Chip color */
  color: ChipProps['color'];
  /** Show loading spinner */
  loading?: boolean;
  /** Tooltip description */
  description: string;
}

/**
 * Status configuration map
 */
const STATUS_CONFIG: Record<CustomDomainStatus, StatusConfig> = {
  pending_dns: {
    label: 'Pending DNS',
    color: 'warning',
    description: 'Waiting for you to add the DNS record',
  },
  verifying_dns: {
    label: 'Verifying DNS',
    color: 'warning',
    loading: true,
    description: 'Checking if DNS record has propagated',
  },
  dns_verified: {
    label: 'DNS Verified',
    color: 'info',
    description: 'DNS verified, starting certificate provisioning',
  },
  provisioning_cert: {
    label: 'Creating SSL',
    color: 'info',
    loading: true,
    description: 'Provisioning SSL certificate (may take 15-30 minutes)',
  },
  configuring_lb: {
    label: 'Configuring',
    color: 'info',
    loading: true,
    description: 'Adding domain to load balancer',
  },
  active: {
    label: 'Active',
    color: 'success',
    description: 'Domain is fully operational',
  },
  failed: {
    label: 'Failed',
    color: 'error',
    description: 'Domain setup failed - check error details',
  },
  suspended: {
    label: 'Suspended',
    color: 'default',
    description: 'Domain is temporarily disabled',
  },
};

/**
 * Component props
 */
export interface DomainStatusBadgeProps {
  /** Current domain status */
  status: CustomDomainStatus;
  /** Chip size */
  size?: 'small' | 'medium';
  /** Show full label (default) or abbreviated */
  variant?: 'outlined' | 'filled';
}

/**
 * Domain Status Badge Component
 *
 * Displays domain status with appropriate color coding and loading indicator.
 */
export const DomainStatusBadge: React.FC<DomainStatusBadgeProps> = ({
  status,
  size = 'small',
  variant = 'outlined',
}) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.failed;

  return (
    <Chip
      label={config.label}
      color={config.color}
      size={size}
      variant={variant}
      icon={
        config.loading ? (
          <CircularProgress
            size={size === 'small' ? 12 : 16}
            color="inherit"
            sx={{ ml: 0.5 }}
          />
        ) : undefined
      }
      title={config.description}
    />
  );
};

/**
 * Get status description for display
 */
export function getStatusDescription(status: CustomDomainStatus): string {
  return STATUS_CONFIG[status]?.description || 'Unknown status';
}

/**
 * Check if status is terminal (no more changes expected)
 */
export function isTerminalStatus(status: CustomDomainStatus): boolean {
  return status === 'active' || status === 'failed' || status === 'suspended';
}

/**
 * Check if status indicates success
 */
export function isSuccessStatus(status: CustomDomainStatus): boolean {
  return status === 'active';
}

/**
 * Check if status indicates error
 */
export function isErrorStatus(status: CustomDomainStatus): boolean {
  return status === 'failed';
}

export default DomainStatusBadge;
